import React from 'react';
import { Navigate, useOutletContext, useLocation } from 'react-router';
import {
  Box,
  Container,
  Typography,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { useUser, useAuth } from '@/providers';

import Sidebar from './Sidebar';

function ProtectedRoute({ children }) {
  const location = useLocation();
  const session = useAuth();
  const user = useUser();
  const { drawerOpen, toggleDrawer, pinned, togglePin } =
    useOutletContext() || {};
  const theme = useTheme();

  if (
    session?.explicitlyLoggedOut &&
    location.pathname !== '/' &&
    location.pathname !== '/logout'
  ) {
    return <Navigate to='/' replace />;
  }

  // If no session and not on "/" route, show loading (redirect is in progress)
  if (!session?.isLoggedIn) {
    if (location.pathname === '/') {
      // Allow Landing page to render
      return <>{children}</>;
    }
    // Show loading while redirecting to signin
    return (
      <Container>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant='h6'>Redirecting to sign in...</Typography>
        </Box>
      </Container>
    );
  }

  // If session exists but no user yet, show loading
  if (!user) {
    return (
      <Container>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant='h6'>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  // Handle user status checks
  if (user.status === 'banned') {
    return (
      <Container>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant='h3' align='center'>
            Your account has been disabled.
          </Typography>
          <Typography variant='h6' align='center' sx={{ mt: 2 }}>
            Please contact us at support@
            {import.meta.env.VITE_DOMAIN_SUFFIX}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (user.status === 'new') {
    return <Navigate to='/account/signup' replace />;
  }

  if (user.status !== 'approved') {
    return <Navigate to='/' replace />;
  }

  const sidebar_open = drawerOpen || pinned;
  return (
    <Box
      sx={{
        display: 'flex',
        height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`,
        overflow: 'hidden',
      }}
    >
      <Sidebar
        open={drawerOpen}
        toggleDrawer={toggleDrawer}
        pinned={pinned}
        togglePin={togglePin}
      />
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          p: 3,
          width: '100%',
          height: '100%',
          overflow: 'auto',
          marginLeft: {
            xs: 0,
            md: sidebar_open ? '240px' : 0,
          },
          transition: (theme) =>
            theme.transitions.create('margin-left', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default ProtectedRoute;
